<template>
  <b-modal
    v-model="editorShown"
    :title="urId ? 'Edit Review' : 'New Review'"
    centered
    size="lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer>
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="text-left mt-1 mr-2">
          <a
            v-show="!urId"
            class="ml-2 hover-pointer"
            @click="loadLastUr"
            variant="primary"
            :class="!eoc ? 'disabled' : ''"
            style="font-size: 14px; text-decoration: underline;"
          >
            Load info from last review
          </a>
          <a
            v-show="urId"
            class="ml-2 text-primary hover-pointer"
            @click="createNextReview"
            variant="primary"
            style="font-size: 14px; text-decoration: underline;"
          >
            Create next review
          </a>
        </div>
        <div class="text-right mt-1 mr-2">
          <b-button @click="$emit('onModify')" variant="secondary">
            Cancel
          </b-button>
          <b-button class="ml-2" @click="submitURAndClose" variant="primary">
            Save
          </b-button>
        </div>
      </div>
    </template>
    <div class="d-flex">
      <div class="mr-4" style="width: calc(100% - 328px);">
        <b-row>
          <b-col cols="4">
            <b-form-group
              label="Patient"
              :state="formErrors.eoc.length == 0 ? null : false"
              :invalid-feedback="formErrors.eoc.join(', ')"
            >
              <template v-slot:label>
                Patient <span class="text-danger">*</span>
              </template>
              <eoc-picker
                :eocs="clientOptions"
                v-model="eoc"
                :async="true"
                @search="
                  ({ search, loading }) =>
                    fetchClientOptionsPicker(search, loading)
                "
              />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group
              label="Insurance"
              :state="formErrors.insuranceId.length == 0 ? null : false"
              :invalid-feedback="formErrors.insuranceId.join(', ')"
            >
              <v-select
                v-model="insurance"
                :options="insuranceOptions"
              ></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Status">
              <v-select v-model="form.status" :options="utilizationStatuses" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group
              label="Review Date"
              :state="formErrors.reviewDate.length == 0 ? null : false"
              :invalid-feedback="formErrors.reviewDate.join(', ')"
            >
              <template v-slot:label>
                Review Date <span class="text-danger">*</span>
              </template>
              <date-picker
                v-model="form.reviewDate"
                :errors="formErrors.reviewDate"
              />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Review Type">
              <v-select v-model="reviewType" :options="reviewTypes"></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group
              label="Utilization Reviewer"
              :state="formErrors.reviewer.length == 0 ? null : false"
              :invalid-feedback="formErrors.reviewer.join(', ')"
            >
              <v-select
                v-model="form.reviewerId"
                :options="reviewers"
              ></v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group
              label="Service"
              :state="formErrors.levelOfCare.length == 0 ? null : false"
              :invalid-feedback="formErrors.levelOfCare.join(', ')"
            >
              <template v-slot:label>
                Service <span class="text-danger">*</span>
              </template>
              <v-select
                class="flex-grow-1"
                v-model="form.levelOfCare"
                :options="levelsOfCare"
              ></v-select>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Authorization number">
              <b-form-input
                v-model="form.authorizationNumber"
                trim
                maxLength="30"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group
              label="Next Review Date (Due Date)"
              :state="formErrors.nextReviewDate.length == 0 ? null : false"
              :invalid-feedback="formErrors.nextReviewDate.join(', ')"
            >
              <date-picker
                v-model="form.nextReviewDate"
                :errors="formErrors.nextReviewDate"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group
              label="Provider"
              :state="formErrors.approvedDays.length == 0 ? null : false"
              :invalid-feedback="formErrors.approvedDays.join(', ')"
            >
              <template v-slot:label>
                Provider
              </template>
              <v-select
                :options="providers"
                v-model="form.providerId"
                :reduce="provider => provider.value"
              />
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group
              label="Number of Units"
              :state="formErrors.approvedDays.length == 0 ? null : false"
              :invalid-feedback="formErrors.approvedDays.join(', ')"
            >
              <template v-slot:label>
                Number of Units <span class="text-danger">*</span>
              </template>
              <b-form-input
                type="number"
                v-model="numberOfUnits"
                :min="form.assignedDates.length"
                trim
                @blur="checkNonEmpty"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group
              label="Approval Range Start Date"
              :state="formErrors.startDate.length == 0 ? null : false"
              :invalid-feedback="formErrors.startDate.join(', ')"
            >
              <date-picker
                v-model="startDateComputed"
                :errors="formErrors.startDate"
                :max="form.endDate ? form.endDate : null"
              />
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group
              label="Approval Range End Date"
              :state="formErrors.endDate.length == 0 ? null : false"
              :invalid-feedback="formErrors.endDate.join(', ')"
            >
              <date-picker
                v-model="endDateComputed"
                :errors="formErrors.endDate"
                :min="form.startDate ? form.startDate : null"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <fieldset class="group-box">
              <legend>Contact Information</legend>
              <b-form-group label="Case Manager" style="margin-top: 10px;">
                <b-form-input v-model="form.caseManager" trim></b-form-input>
              </b-form-group>
              <b-form-group
                label="Contact Phone Number"
                :state="
                  formErrors.contactPhoneNumber.length == 0 ? null : false
                "
                :invalid-feedback="formErrors.contactPhoneNumber.join(', ')"
              >
                <phone-input
                  ref="phoneInput"
                  defaultCountry="US"
                  v-model="form.contactPhoneNumber"
                  trim
                  :state="
                    formErrors.contactPhoneNumber.length == 0 ? null : false
                  "
                ></phone-input>
              </b-form-group>
            </fieldset>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Notes">
              <b-form-textarea
                style="height:202px"
                v-model="form.notes"
                trim
              ></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <div class="assign-dates-container bg-extra-light-gray p-4">
        <div
          class="d-flex align-items-center justify-content-between w-100 bg-white p-2"
        >
          <div class="d-flex align-items-center">
            <div
              class="text-primary"
              style="font-size: 28px; line-height:33px;"
            >
              {{ remainingDays }}
            </div>
            <div class="ml-1">
              <div style="font-size:13px; line-height:15px;">
                Available units
              </div>
            </div>
          </div>
          <b-dropdown
            size="sm"
            right
            boundary="viewport"
            text="Assign"
            variant="outline-primary"
            class="utilization-dropdown"
            ref="autoFillDropdown"
            :disabled="remainingDays == 0"
            @show="showAutoFill = true"
            @hide="showAutoFill = false"
          >
            <b-dropdown-form>
              <utilization-auto-fill-dropdown-content
                v-if="showAutoFill"
                :numberOfUnits="remainingDays"
                :startDate="form.startDate"
                :endDate="form.endDate"
                :restrictDates="true"
                @autoFill="autoFill"
              />
            </b-dropdown-form>
          </b-dropdown>
        </div>
        <calendar-picker
          :disabled="remainingDays <= 0 || isCalendarDisable"
          v-model="form.assignedDates"
          :key="calendarKey"
          :minDate="this.form.startDate"
          :maxDate="this.form.endDate"
        />
        <div class="d-flex justify-content-end my-2">
          <a
            class="text-danger hover-pointer"
            @click="clearAssignedDays"
            style="font-size:14px;"
            >Clear all units</a
          >
        </div>
        <div
          class="d-flex align-items-center justify-content-between w-100 bg-white p-2"
        >
          <div style="font-size:13px; line-height:15px;">
            Assigned Units:
          </div>
        </div>
        <div style="height: 165px; overflow-y: auto;">
          <div class="d-flex flex-wrap w-100 p-2">
            <div
              class="d-flex align-items-center"
              v-for="(date, dateIndex) in form.assignedDates"
              :key="date"
              style="width: 50%"
            >
              <span>{{ formatDateOnly(date) }}</span>
              <img
                :src="imageDelete"
                width="17"
                height="17"
                class="pb-1 pl-1 hover-pointer"
                @click="deleteAssignedDate(dateIndex)"
              />
            </div>
          </div>
        </div>
        <b-alert
          v-if="form.approvedDays < form.assignedDates.length"
          variant="danger"
          show
          style="font-size: 13px;"
          class="mt-4 mb-0"
        >
          {{ form.assignedDates.length - form.approvedDays }} more units
          assigned than approved.
        </b-alert>
      </div>
    </div>
  </b-modal>
</template>

<script>
import urRecordServer from "../../server/urRecord";
import userServer from "../../server/user";
import patientServer from "../../server/patient";
import eocServer from "../../server/eoc";
import { mapActions, mapGetters, mapState } from "vuex";
import DatePicker from "../controls/DatePicker";
import {
  validateRequired,
  validateDate,
  validatePhoneNumber
} from "../../helpers/validation";
import EocPicker from "../controls/EocPicker";
import { formatDateOnlyMysql } from "../../helpers/date";
import UtilizationAutoFillDropdownContent from "./UtilizationAutoFillDropdownContent.vue";
import CalendarPicker from "../controls/CalendarPicker";
import PhoneInput from "../controls/PhoneInput.vue";
import serviceServer from "../../server/service";
export default {
  name: "UtilizationAddEditModal",
  components: {
    EocPicker,
    DatePicker,
    UtilizationAutoFillDropdownContent,
    CalendarPicker,
    PhoneInput
  },
  props: ["urId", "lastUrId"],
  data() {
    return {
      submit: null,
      calendarKey: null,
      form: {
        episodeOfCareId: null,
        insuranceId: null,
        caseManager: "",
        contactPhoneNumber: "",
        reviewTypeId: null,
        reviewDate: null,
        nextReviewDate: null,
        startDate: null,
        endDate: null,
        approvedDays: "",
        authorizationNumber: "",
        notes: "",
        levelOfCare: null,
        reviewerId: null,
        status: null,
        assignedDates: [],
        providerId: null
      },
      formErrors: {
        eoc: [],
        insuranceId: [],
        reviewDate: [],
        nextReviewDate: [],
        startDate: [],
        endDate: [],
        levelOfCare: [],
        reviewer: [],
        contactPhoneNumber: [],
        approvedDays: []
      },
      insuranceOptions: [],
      clientOptions: [],
      clientSearchChanged: false,
      users: [],
      hasUnsavedChanges: false,
      dataLoaded: false,
      showAutoFill: false,
      services: [],
      isCalendarDisable: false
    };
  },
  async mounted() {
    await this.getUsers();
    await this.fetchServices();
    await this.fetchClientOptions();

    if (this.urId) {
      const ur = await urRecordServer.get(this.urId);
      this.form = {
        episodeOfCareId: ur.episodeOfCare
          ? {
              value: ur.episodeOfCare.id,
              label: this.formatClient(
                ur.episodeOfCare.patient.firstName,
                ur.episodeOfCare.patient.middleName,
                ur.episodeOfCare.patient.preferredName,
                ur.episodeOfCare.patient.lastName,
                ur.episodeOfCare.patient.mrn
              ),
              data: ur.episodeOfCare
            }
          : null,
        insuranceId: ur.insurance ? ur.insurance.id : null,
        caseManager: ur.caseManager,
        reviewTypeId: ur.reviewType ? ur.reviewType.id : null,
        reviewDate: ur.reviewDate,
        nextReviewDate: ur.nextReviewDate,
        startDate: ur.startDate,
        endDate: ur.endDate,
        approvedDays: ur.numberOfUnits,
        authorizationNumber: ur.authorizationNo,
        notes: ur.notes,
        levelOfCare: ur.service
          ? ur.service.type == "Service"
            ? this.services.find(s => s.value == ur.service.id)
            : {
                value: ur.service.id,
                label: ur.service.name,
                color: ur.service.color,
                order: ur.service.order,
                type: ur.service.type
              }
          : null,
        reviewerId: ur.reviewer
          ? {
              value: ur.reviewer.id,
              label: this.formatUser(ur.reviewer)
            }
          : null,
        status: ur.utilizationStatus
          ? {
              value: ur.utilizationStatus.id,
              label: ur.utilizationStatus.name,
              color: ur.utilizationStatus.color,
              order: ur.utilizationStatus.order
            }
          : null,
        contactPhoneNumber: ur.contactPhoneNumber ? ur.contactPhoneNumber : "",
        assignedDates: ur.scheduleEvents.map(scheduleEvent =>
          formatDateOnlyMysql(scheduleEvent.startDateTime)
        ),
        providerId: ur.providerId
      };
      await this.fetchInsuranceOptions();
      this.$nextTick(function() {
        this.$watch(
          "form",
          function() {
            if (this.dataLoaded) {
              this.hasUnsavedChanges = true;
            }
            this.dataLoaded = true;
          },
          { deep: true }
        );
      });
    } else if (this.lastUrId) {
      await this.getUr(this.lastUrId);
      this.fetchInsuranceOptions();
    } else {
      this.form.reviewDate = formatDateOnlyMysql(
        this.getCurrentAdjustedDateTime()
      );
      this.form.approvedDays = 1;
      if (this.currentUser) {
        this.form.reviewerId = {
          value: this.currentUser.id,
          label: this.formatUser(this.currentUser)
        };
      }
      this.form.reviewTypeId = this.reviewTypes.find(
        reviewType => reviewType.label == "Initial"
      ).value;
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    clearAssignedDays() {
      this.form.assignedDates = [];
      this.calendarKey += 1;
    },
    async fetchServices() {
      const services = await serviceServer.query({
        type: "Service"
      });
      this.services = services.map(service => ({
        value: service.id,
        label: service.name,
        type: "Service"
      }));
    },
    updateAssignedDates() {
      this.form.assignedDates = this.form.assignedDates.filter(item => {
        return (
          new Date(item).getTime() >= new Date(this.form.startDate).getTime() &&
          new Date(item).getTime() <= new Date(this.form.endDate).getTime()
        );
      });
    },
    validateURSubmit() {
      let withoutErrors = true;

      this.formErrors.eoc = [];
      if (!validateRequired(this.form.episodeOfCareId)) {
        this.formErrors.eoc.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.approvedDays = [];
      if (!validateRequired(this.form.approvedDays)) {
        this.formErrors.approvedDays.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.reviewDate = [];
      if (!validateRequired(this.form.reviewDate)) {
        this.formErrors.reviewDate.push("This field is required");
        withoutErrors = false;
      }
      if (this.form.reviewDate && !validateDate(this.form.reviewDate)) {
        this.formErrors.reviewDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      this.formErrors.startDate = [];
      if (this.form.startDate && !validateDate(this.form.startDate)) {
        this.formErrors.startDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      this.formErrors.endDate = [];
      if (this.form.endDate && !validateDate(this.form.endDate)) {
        this.formErrors.endDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      this.formErrors.nextReviewDate = [];
      if (this.form.nextReviewDate && !validateDate(this.form.nextReviewDate)) {
        this.formErrors.nextReviewDate.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      this.formErrors.levelOfCare = [];
      if (!validateRequired(this.form.levelOfCare)) {
        this.formErrors.levelOfCare.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.contactPhoneNumber = [];
      if (
        this.form.contactPhoneNumber?.trim().length >
          this.activeCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.contactPhoneNumber)
      ) {
        this.formErrors.contactPhoneNumber.push("Invalid phone number");
        withoutErrors = false;
      }

      if (this.form.approvedDays < this.form.assignedDates.length) {
        this.showError({
          title: "Validation error",
          message: `There is ${this.form.assignedDates.length -
            this.form
              .approvedDays} more units assigned than approved. Please correct before saving.`
        });
        withoutErrors = false;
      }

      if (this.isCalendarDisable && this.form.assignedDates.length > 0) {
        this.showError({
          title: "Validation error",
          message: "Calendar only available for Levels of Care."
        });
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitURAndClose() {
      const result = await this.submitUR();
      if (result) this.$emit("onModify");
    },
    async submitUR() {
      const result = await this.validateURSubmit();
      if (!result) {
        return false;
      }

      const formData = {
        episodeOfCareId: this.form.episodeOfCareId.value,
        insuranceId: this.form.insuranceId,
        reviewTypeId: this.form.reviewTypeId,
        reviewDate: this.form.reviewDate,
        reviewerId: this.form.reviewerId ? this.form.reviewerId.value : null,
        caseManager: this.form.caseManager,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        nextReviewDate: this.form.nextReviewDate,
        numberOfUnits: this.form.approvedDays
          ? parseInt(this.form.approvedDays)
          : 0,
        authorizationNo: this.form.authorizationNumber,
        notes: this.form.notes,
        serviceId: this.form.levelOfCare.value,
        utilizationStatusId: this.form.status ? this.form.status.value : null,
        contactPhoneNumber:
          this.form.contactPhoneNumber?.trim().length >
          this.activeCountry.dialCode.length + 1
            ? this.form.contactPhoneNumber
            : null,
        assignedDates: this.form.assignedDates,
        providerId: this.form.providerId
      };

      if (this.urId) await urRecordServer.update(this.urId, formData);
      else await urRecordServer.create(formData);

      this.showAlertSuccess();
      return true;
    },
    async getUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users;
    },
    async fetchClientOptions(search) {
      this.clientSearchChanged = true;
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );
          this.clientOptions = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.clientSearchChanged = false;
        } else {
          this.clientSearchChanged = false;
          this.clientOptions = [];
        }
      }, 1000);
    },
    async fetchClientOptionsPicker(search, loading) {
      if (this.lastTimeoutClientOptionsPicker) {
        clearTimeout(this.lastTimeoutClientOptionsPicker);
        this.lastTimeoutClientOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.clientOptions = [];
      }

      this.lastTimeoutClientOptionsPicker = setTimeout(async () => {
        loading(true);
        if (!search || search.length == 0 || search.length >= 3) {
          this.searchingClients = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],

              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );
          this.clientOptions = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.searchingClients = false;
        }
        loading(false);
      }, 1000);
    },
    async fetchInsuranceOptions() {
      const optionsResult = await patientServer.get(
        this.form.episodeOfCareId.data.patient.id
      );

      this.insuranceOptions = optionsResult.insurances.map(insurance => ({
        value: insurance.id,
        label:
          (insurance.primary
            ? "Primary - "
            : insurance.secondary
            ? "Secondary - "
            : insurance.tertiary
            ? "Tertiary - "
            : "Other - ") + (insurance.company ? insurance.company.name : "")
      }));
    },
    async loadLastUr() {
      const lastUr = await urRecordServer.getLastUr(
        this.form.episodeOfCareId.value
      );
      this.form.insuranceId = lastUr.insurance ? lastUr.insurance.id : null;
      this.form.caseManager = lastUr.caseManager ? lastUr.caseManager : null;
      this.form.notes = lastUr.notes;
      this.form.reviewTypeId = this.reviewTypes.find(
        reviewType => reviewType.label == "Review"
      ).value;
      this.form.levelOfCare = lastUr.service
        ? {
            value: lastUr.service.id,
            label: lastUr.service.name,
            color: lastUr.service.color,
            order: lastUr.service.order
          }
        : null;
      this.form.reviewerId = lastUr.reviewer
        ? {
            value: lastUr.reviewer.id,
            label: this.formatUser(lastUr.reviewer)
          }
        : null;
      this.form.contactPhoneNumber = lastUr.contactPhoneNumber
        ? lastUr.contactPhoneNumber
        : null;
      this.form.authorizationNumber = lastUr.authorizationNo
        ? lastUr.authorizationNo
        : null;
    },
    async getUr(id) {
      const ur = await urRecordServer.get(id);
      this.form.insuranceId = ur.insurance ? ur.insurance.id : null;
      this.form.caseManager = ur.caseManager ? ur.caseManager : null;
      this.form.reviewTypeId = ur.reviewType ? ur.reviewType.id : null;
      this.form.levelOfCare = ur.levelOfCare
        ? {
            value: ur.levelOfCare.id,
            label: ur.levelOfCare.name,
            color: ur.levelOfCare.color,
            order: ur.levelOfCare.order
          }
        : null;
      this.form.reviewerId = ur.reviewer
        ? {
            value: ur.reviewer.id,
            label: this.formatUser(ur.reviewer)
          }
        : null;
      this.form.contactPhoneNumber = ur.contactPhoneNumber
        ? ur.contactPhoneNumber
        : null;
      this.form.authorizationNumber = ur.authorizationNo
        ? ur.authorizationNo
        : null;
      this.form.episodeOfCareId = ur.episodeOfCare
        ? {
            value: ur.episodeOfCare.id,
            label: this.formatClient(
              ur.episodeOfCare.patient.firstName,
              ur.episodeOfCare.patient.middleName,
              ur.episodeOfCare.patient.preferredName,
              ur.episodeOfCare.patient.lastName,
              ur.episodeOfCare.patient.mrn
            ),
            data: ur.episodeOfCare
          }
        : null;
      this.form.notes = ur.notes;
      this.form.reviewDate = formatDateOnlyMysql(
        this.getCurrentAdjustedDateTime()
      );
    },
    async createNextReview() {
      if (this.hasUnsavedChanges) {
        this.$dialog
          .confirm(
            {
              title: "Do you want to save your changes?"
            },
            {
              view: "confirm-unsaved-changes"
            }
          )
          .then(async ({ data }) => {
            if (data.confirmValue) {
              await this.submitUR();
            }
            this.$emit("onNextReview", this.urId);
          })
          .catch(function() {});
      } else {
        this.$emit("onNextReview", this.urId);
      }
    },
    deleteAssignedDate(dateIndex) {
      const assignedDates = [...this.form.assignedDates];
      assignedDates.splice(dateIndex, 1);
      this.form.assignedDates = assignedDates;
    },
    autoFill(assignedDates) {
      this.form.assignedDates = this.form.assignedDates.concat(
        assignedDates.filter(item => this.form.assignedDates.indexOf(item) < 0)
      );
      this.$refs.autoFillDropdown.hide(true);
    },
    checkNonEmpty() {
      if (this.form.approvedDays == "" || !this.form.approvedDays) {
        this.form.approvedDays = 1;
      }
    }
  },
  computed: {
    ...mapState("client", {
      reviewTypes: state =>
        state.reviewTypes.map(reviewType => ({
          value: reviewType.id,
          label: reviewType.name
        })),
      utilizationStatuses: state =>
        state.utilizationStatuses.map(status => ({
          value: status.id,
          label: status.name
        }))
    }),
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    ...mapGetters("client", ["levelsOfCareCurrentLocation"]),
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    levelsOfCare() {
      return [
        ...this.levelsOfCareCurrentLocation.map(levelOfCare => ({
          value: levelOfCare.id,
          label: levelOfCare.name,
          type: "LevelOfCare"
        })),
        ...this.services
      ];
    },
    numberOfUnits: {
      get() {
        return this.form.approvedDays;
      },
      set(value) {
        this.form.approvedDays = value;
        this.updateAssignedDates();
      }
    },
    startDateComputed: {
      get() {
        return this.form.startDate;
      },
      set(value) {
        this.form.startDate = value;
        this.updateAssignedDates();
      }
    },
    endDateComputed: {
      get() {
        return this.form.endDate;
      },
      set(value) {
        this.form.endDate = value;
        this.updateAssignedDates();
      }
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    eoc: {
      get() {
        return this.form.episodeOfCareId;
      },
      set(newValue) {
        this.form.episodeOfCareId = newValue;
        if (newValue) this.fetchInsuranceOptions();
      }
    },
    insurance: {
      get() {
        return this.form.insuranceId
          ? this.insuranceOptions.find(
              insurance => insurance.value == this.form.insuranceId
            )
          : null;
      },
      set(newValue) {
        this.form.insuranceId = newValue ? newValue.value : null;
      }
    },
    reviewType: {
      get() {
        return this.form.reviewTypeId
          ? this.reviewTypes.find(
              reviewType => reviewType.value == this.form.reviewTypeId
            )
          : null;
      },
      set(newValue) {
        this.form.reviewTypeId = newValue ? newValue.value : null;
      }
    },
    remainingDays() {
      return this.form.approvedDays > this.form.assignedDates.length
        ? this.form.approvedDays - this.form.assignedDates.length
        : 0;
    },
    activeCountry() {
      return this.$refs.phoneInput.$refs.telInput.activeCountry;
    },
    providers() {
      return this.users
        .filter(u => u.providerInfo)
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }));
    },
    reviewers() {
      return this.users.map(user => ({
        value: user.id,
        label: this.formatUser(user)
      }));
    },
    imageDelete() {
      return this.isStandalone()
        ? "/icons/delete.svg"
        : "/desktop/icons/delete.svg";
    }
  },
  watch: {
    "form.levelOfCare": function(newVal, oldValue) {
      if (newVal != oldValue) {
        if (newVal?.type === "Service") {
          this.isCalendarDisable = true;
          this.form.assignedDates = [];
        } else this.isCalendarDisable = false;
      }
    }
  }
};
</script>

<style scoped>
.assign-dates-container {
  width: 320px;
  margin-top: -17px;
  margin-right: -15px;
  margin-bottom: -16px;
}

.utilization-dropdown >>> ul {
  left: -142px !important;
}

.v-select >>> .vs__dropdown-menu li {
  white-space: normal;
}
</style>
